import React, {
  useEffect,
  useState,
} from "react";

import {
  useParams,
  Link,
} from "react-router-dom";

import { useTranslation } from "react-i18next";

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import { db } from "../firebase/firebase";

import "./GalleryDetails.css";

function GalleryDetails() {
  const {
    type,
    category,
    albumId,
  } = useParams();

  const {
    t,
    i18n,
  } = useTranslation();

  // =====================================================
  // DECODE URL
  // =====================================================

  const decodedType =
    decodeURIComponent(type || "");

  const decodedCategory =
    decodeURIComponent(category || "");

  const isArabic =
    i18n.language === "ar";

  // =====================================================
  // STATE
  // =====================================================

  const [album, setAlbum] =
    useState(null);

  const [similarAlbums, setSimilarAlbums] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [currentImage, setCurrentImage] =
    useState(0);

  // =====================================================
  // CATEGORY TRANSLATION
  // =====================================================

  const getCategoryName = (
    categoryName
  ) => {
    const categoryTranslations = {
      Weddings: t(
        "photography.categories.weddings.name"
      ),

      "Islamic Weddings": t(
        "photography.categories.islamicWeddings.name"
      ),

      Engagements: t(
        "photography.categories.engagements.name"
      ),

      "Bridal Sessions": t(
        "photography.categories.bridalSessions.name"
      ),

      "Women Events": t(
        "photography.categories.womenEvents.name"
      ),

      "Family & Friends": t(
        "photography.categories.familyFriends.name"
      ),

      "Bridal Robes": t(
        "rental.categories.bridalRobes.name"
      ),

      "Bridesmaids Robe": t(
        "rental.categories.bridesmaids.name"
      ),

      "Bridesmaids Robes": t(
        "rental.categories.bridesmaids.name"
      ),

      Accessories: t(
        "rental.categories.accessories.name"
      ),
    };

    return (
      categoryTranslations[
        categoryName
      ] || categoryName
    );
  };

  // =====================================================
  // TYPE TRANSLATION
  // =====================================================

  const getTypeName = (
    typeName
  ) => {
    if (
      typeName.toLowerCase() ===
      "photography"
    ) {
      return t(
        "nav.photography"
      );
    }

    if (
      typeName.toLowerCase() ===
      "rental"
    ) {
      return t(
        "nav.rental"
      );
    }

    return typeName;
  };

  // =====================================================
  // LOAD ALBUM
  // =====================================================

  useEffect(() => {
    const loadAlbum =
      async () => {
        try {
          setLoading(true);

          // =================================================
          // CURRENT ALBUM
          // =================================================

          const albumRef =
            doc(
              db,
              "gallery",
              albumId
            );

          const albumSnapshot =
            await getDoc(
              albumRef
            );

          if (
            !albumSnapshot.exists()
          ) {
            setAlbum(null);
            return;
          }

          const albumData = {
            id:
              albumSnapshot.id,
            ...albumSnapshot.data(),
          };

          setAlbum(albumData);

          setCurrentImage(0);

          // =================================================
          // SIMILAR ALBUMS
          // =================================================

          const albumType =
            albumData.type ||
            decodedType;

          const albumCategory =
            albumData.category ||
            decodedCategory;

          try {
            const similarQuery =
              query(
                collection(
                  db,
                  "gallery"
                ),
                where(
                  "type",
                  "==",
                  albumType
                ),
                where(
                  "category",
                  "==",
                  albumCategory
                )
              );

            const similarSnapshot =
              await getDocs(
                similarQuery
              );

            const similarData =
              similarSnapshot.docs
                .map(
                  (item) => ({
                    id:
                      item.id,
                    ...item.data(),
                  })
                )
                .filter(
                  (item) =>
                    item.id !==
                    albumId
                );

            setSimilarAlbums(
              similarData
            );
          } catch (error) {
            console.error(
              "SIMILAR ALBUMS ERROR:",
              error
            );

            setSimilarAlbums([]);
          }
        } catch (error) {
          console.error(
            "GALLERY DETAILS ERROR:",
            error
          );

          setAlbum(null);
        } finally {
          setLoading(false);
        }
      };

    if (albumId) {
      loadAlbum();
    }
  }, [
    albumId,
    decodedType,
    decodedCategory,
  ]);

  // =====================================================
  // GET ALL IMAGES
  // =====================================================

  const getAlbumImages =
    () => {
      if (!album) {
        return [];
      }

      if (
        Array.isArray(
          album.images
        ) &&
        album.images.length > 0
      ) {
        return album.images.filter(
          (image) =>
            typeof image ===
              "string" &&
            image.trim() !== ""
        );
      }

      if (
        typeof album.image ===
          "string" &&
        album.image.trim() !== ""
      ) {
        return [album.image];
      }

      return [];
    };

  const images =
    getAlbumImages();

  // =====================================================
  // NEXT IMAGE
  // =====================================================

  const nextImage = () => {
    if (
      images.length === 0
    ) {
      return;
    }

    setCurrentImage(
      (previous) => {
        if (
          previous >=
          images.length - 1
        ) {
          return 0;
        }

        return previous + 1;
      }
    );
  };

  // =====================================================
  // PREVIOUS IMAGE
  // =====================================================

  const previousImage =
    () => {
      if (
        images.length === 0
      ) {
        return;
      }

      setCurrentImage(
        (previous) => {
          if (
            previous <= 0
          ) {
            return (
              images.length - 1
            );
          }

          return previous - 1;
        }
      );
    };

  // =====================================================
  // KEYBOARD
  // =====================================================

  useEffect(() => {
    const handleKeyboard =
      (event) => {
        if (
          event.key ===
          "ArrowRight"
        ) {
          nextImage();
        }

        if (
          event.key ===
          "ArrowLeft"
        ) {
          previousImage();
        }
      };

    window.addEventListener(
      "keydown",
      handleKeyboard
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyboard
      );
    };
  }, [
    images.length,
  ]);

  // =====================================================
  // WHATSAPP
  // =====================================================

  const handleBooking =
    () => {
      const phoneNumber =
        "96170210576";

      const message =
        encodeURIComponent(
          `مرحباً، أريد الاستفسار عن حجز جلسة ${
            album?.title || ""
          } ضمن قسم ${
            album?.category ||
            decodedCategory
          }.`
        );

      window.open(
        `https://wa.me/${phoneNumber}?text=${message}`,
        "_blank"
      );
    };

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <main className="details-page">

        <div className="details-loading">
          {t(
            "gallery.loading"
          )}
        </div>

      </main>
    );
  }

  // =====================================================
  // NOT FOUND
  // =====================================================

  if (!album) {
    return (
      <main className="details-page">

        <div className="details-empty">

          <span>✦</span>

          <h1>
            {t(
              "gallery.notFound"
            )}
          </h1>

          <Link
            to={`/gallery/${encodeURIComponent(
              decodedType
            )}/${encodeURIComponent(
              decodedCategory
            )}`}
          >
            {t(
              "gallery.backToGallery"
            )}
          </Link>

        </div>

      </main>
    );
  }

  // =====================================================
  // CURRENT DATA
  // =====================================================

  const currentCategory =
    album.category ||
    decodedCategory;

  const currentType =
    album.type ||
    decodedType;

  const translatedCategory =
    getCategoryName(
      currentCategory
    );

  const translatedType =
    getTypeName(
      currentType
    );

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <main className="details-page">

      {/* =================================================
          BACK
      ================================================= */}

      <div className="details-top">

        <Link
          to={`/gallery/${encodeURIComponent(
            currentType
          )}/${encodeURIComponent(
            currentCategory
          )}`}
          className="back-link"
        >
          {isArabic ? "→" : "←"}{" "}
          {t("gallery.back")}{" "}
          {translatedCategory}
        </Link>

      </div>

      {/* =================================================
          MAIN CONTENT
      ================================================= */}

      <section className="details-content">

        {/* =================================================
            GALLERY
        ================================================= */}

        <div className="details-gallery">

          {images.length > 0 ? (

            <>

              {/* MAIN PHOTO */}

              <div className="main-photo">

                <img
                  src={
                    images[
                      currentImage
                    ]
                  }
                  alt={`${album.title} ${
                    currentImage + 1
                  }`}
                />

                {/* LEFT */}

                <button
                  type="button"
                  className="gallery-arrow gallery-arrow-left"
                  onClick={
                    previousImage
                  }
                  aria-label={t(
                    "gallery.previous"
                  )}
                >
                  ‹
                </button>

                {/* RIGHT */}

                <button
                  type="button"
                  className="gallery-arrow gallery-arrow-right"
                  onClick={
                    nextImage
                  }
                  aria-label={t(
                    "gallery.next"
                  )}
                >
                  ›
                </button>

                {/* COUNTER */}

                <div className="image-counter">

                  {String(
                    currentImage + 1
                  ).padStart(
                    2,
                    "0"
                  )}

                  {" / "}

                  {String(
                    images.length
                  ).padStart(
                    2,
                    "0"
                  )}

                </div>

              </div>

              {/* =================================================
                  THUMBNAILS
              ================================================= */}

              {images.length > 1 && (

                <div className="details-thumbnails">

                  {images.map(
                    (
                      image,
                      index
                    ) => (

                      <button
                        key={`${image}-${index}`}
                        type="button"
                        className={`thumbnail ${
                          currentImage ===
                          index
                            ? "active"
                            : ""
                        }`}
                        onClick={() =>
                          setCurrentImage(
                            index
                          )
                        }
                      >

                        <img
                          src={image}
                          alt={`${t(
                            "gallery.image"
                          )} ${
                            index + 1
                          }`}
                        />

                      </button>

                    )
                  )}

                </div>

              )}

            </>

          ) : (

            <div className="no-images">

              <span>
                ✦
              </span>

              <p>
                {t(
                  "gallery.noPhotos"
                )}
              </p>

            </div>

          )}

        </div>

        {/* =================================================
            INFO
        ================================================= */}

        <div className="details-info">

          <span className="details-category">
            {translatedType} ·{" "}
            {translatedCategory}
          </span>

          <h1>
            {album.title}
          </h1>

          <div className="details-divider">

            <span></span>

            <i>✦</i>

            <span></span>

          </div>

          {album.description && (
            <p>
              {album.description}
            </p>
          )}

          <div className="details-photo-count">

            {images.length}{" "}

            {images.length === 1
              ? t(
                  "gallery.photograph"
                )
              : t(
                  "gallery.photographs"
                )}

          </div>

          <button
            type="button"
            className="book-button"
            onClick={
              handleBooking
            }
          >

            <span>
              {t(
                "gallery.bookNow"
              )}
            </span>

            <strong>
              ↗
            </strong>

          </button>

        </div>

      </section>

      {/* =================================================
          YOU MAY ALSO LIKE
      ================================================= */}

      {similarAlbums.length >
        0 && (

        <section className="similar-section">

          <div className="similar-heading">

            <span>
              {t(
                "gallery.youMayAlsoLike"
              )}
            </span>

            <h2>
              {t(
                "gallery.more"
              )}{" "}
              {translatedCategory}
            </h2>

            <div className="similar-divider">

              <span></span>

              <i>✦</i>

              <span></span>

            </div>

          </div>

          <div className="similar-grid">

            {similarAlbums.map(
              (
                similar,
                index
              ) => {

                const similarImages =
                  Array.isArray(
                    similar.images
                  )
                    ? similar.images
                    : similar.image
                    ? [
                        similar.image,
                      ]
                    : [];

                const similarCategory =
                  getCategoryName(
                    similar.category ||
                      currentCategory
                  );

                return (

                  <Link
                    key={
                      similar.id
                    }
                    to={`/gallery/${encodeURIComponent(
                      currentType
                    )}/${encodeURIComponent(
                      similar.category ||
                        currentCategory
                    )}/${
                      similar.id
                    }`}
                    className="similar-card"
                  >

                    {/* IMAGE */}

                    <div className="similar-image">

                      {similarImages.length >
                      0 ? (

                        <img
                          src={
                            similarImages[0]
                          }
                          alt={
                            similar.title ||
                            t(
                              "categoryGallery.untitled"
                            )
                          }
                        />

                      ) : (

                        <div className="similar-placeholder">
                          ✦
                        </div>

                      )}

                      <div className="similar-overlay">

                        {t(
                          "gallery.viewGallery"
                        )}{" "}
                        ↗

                      </div>

                    </div>

                    {/* INFO */}

                    <div className="similar-info">

                      <span>
                        {String(
                          index + 1
                        ).padStart(
                          2,
                          "0"
                        )}
                      </span>

                      <h3>
                        {similar.title ||
                          t(
                            "categoryGallery.untitled"
                          )}
                      </h3>

                      <p>
                        {
                          similarCategory
                        }
                      </p>

                    </div>

                  </Link>

                );
              }
            )}

          </div>

        </section>

      )}

    </main>
  );
}

export default GalleryDetails;